import { useRef, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import type { User } from '@/modules/user/userTypes';
import { Popover } from '@/components/atoms/Popover';
import { sendAnalytics } from '@/services/analyticsService';

import {
	logoutUserRequest,
	userToggleAddUserModal,
	userToggleAddBccModal
} from '@/modules/user/userActions';
import { useDispatch } from 'react-redux';
import useOnClickOutside from '@/hooks/useOnClickOutside';

type ProfileModalProps = {
	user: User;
	isOpen: boolean;
	children: ReactNode;
	onClose: () => void;
	onOpenTFAModal: () => void;
	onOpenPasswordResetModal: () => void;
};

export const ProfileModal = ({
	user,
	isOpen,
	onClose,
	onOpenTFAModal,
	onOpenPasswordResetModal,
	children
}: ProfileModalProps) => {
	const dispatch = useDispatch();
	const ref = useRef<HTMLDivElement>(null);

	useOnClickOutside({
		ref,
		handler: onClose,
		enabled: isOpen
	});

	const handleOpenAddBccModal = () => {
		if (user && user.email) {
			sendAnalytics({
				type: 'track',
				userId: user.email,
				eventName: 'User pressed Add New BCC button in menu'
			});
		}
		dispatch(userToggleAddBccModal(true));
	};

	const handleOpenTFAModal = () => {
		onOpenTFAModal();

		if (user && user.email) {
			sendAnalytics({
				type: 'track',
				userId: user.email,
				eventName: 'User pressed Two-factor auth button in menu'
			});
		}
	};

	const handleOpenPasswordResetModal = () => {
		onOpenPasswordResetModal();
		if (user && user.email) {
			sendAnalytics({
				type: 'track',
				userId: user.email,
				eventName: 'User pressed Reset Password button in menu'
			});
		}
	};

	// Add new user helpers
	const handleOpenAddUserModal = () => {
		if (user && user.email) {
			sendAnalytics({
				type: 'track',
				userId: user.email,
				eventName: `Opened Add User Modal`
			});
		}
		dispatch(userToggleAddUserModal(true));
	};

	const handleLogout = () => {
		sendAnalytics({
			type: 'track',
			userId: user.email,
			eventName: 'User pressed Log Out button in menu'
		});

		window?.productFruits?.services?.destroy();
		dispatch(logoutUserRequest());
	};

	return (
		<div ref={ref}>
			<Popover.Root open={isOpen}>
				<Popover.Anchor>{children}</Popover.Anchor>

				<Popover.Content className="bg-dm-black1 p-0" align="start">
					<header className="flex items-center justify-between border-b border-gray-700 p-4">
						<h2 className="text-lg font-bold text-dm-white">
							User: {user.email}
						</h2>
					</header>

					<ul className="w-full divide-y divide-gray-700">
						<li className="h-11 text-dm-gray transition-colors hover:text-dm-white">
							<button
								type="button"
								className="inline-flex h-full w-full items-center p-4"
								onClick={handleOpenPasswordResetModal}
							>
								Reset Password
							</button>
						</li>

						{user?.features?.otp && (
							<li className="h-11 text-dm-gray transition-colors hover:text-dm-white">
								<button
									type="button"
									className="inline-flex h-full w-full items-center p-4"
									onClick={handleOpenTFAModal}
								>
									Two-Factor Auth
								</button>
							</li>
						)}

						<li className="h-11 text-dm-gray transition-colors hover:text-dm-white">
							<button
								type="button"
								className="inline-flex h-full w-full items-center p-4"
								onClick={handleOpenAddUserModal}
							>
								Add a New User
							</button>
						</li>

						<li className="h-11 text-dm-gray transition-colors hover:text-dm-white">
							<button
								type="button"
								onClick={handleOpenAddBccModal}
								className="inline-flex h-full w-full items-center p-4"
							>
								{Object.keys(user?.crmBcc || {}).length > 0
									? 'Edit CRM Bcc settings'
									: 'Add CRM Bcc'}
							</button>
						</li>

						{!user?.re2SharedDomain && (
							<li className="h-11 text-dm-gray transition-colors hover:text-dm-white">
								<Link
									to="/verify-email-onboarding"
									className="inline-flex h-full w-full items-center p-4"
								>
									Verify Your Email
								</Link>
							</li>
						)}

						{user?.role === 'Admin' &&
							user?.features?.aiSandbox && (
								<li className="h-11 text-dm-gray transition-colors hover:text-dm-white">
									<Link
										to="/ai-prompts"
										className="inline-flex h-full w-full items-center p-4"
									>
										AI Prompts
									</Link>
								</li>
							)}

						<li className="h-11 text-dm-gray transition-colors hover:text-dm-white">
							<button
								type="button"
								className="inline-flex h-full w-full items-center p-4"
								onClick={handleLogout}
							>
								Log Out
							</button>
						</li>
					</ul>
				</Popover.Content>
			</Popover.Root>
		</div>
	);
};
